/*
 * nadi-sold-out.js — a sold-out product page should still sell something.
 *
 * WHY. 87 active products are out of stock, and one of them is the store's
 * single best seller by order count: the SPIKE ring light, 84 orders and
 * ₪25,435 in 90 days, now at zero. Those pages are indexed, they take traffic
 * from Google, and today they are a dead end — a greyed-out button and a
 * "notify me" field. Someone who searched for a ring light, found one, and
 * discovered it is gone leaves the store, and the store has two other ring
 * lights in stock at ₪399 and ₪369.
 *
 * This does not touch stock, pricing or merchandising. It only refuses to let
 * a page that already has the visitor's attention waste it.
 *
 * ── WHERE THE ALTERNATIVES COME FROM ──────────────────────────────────────
 * Shopify's own recommendations endpoint, /recommendations/products.json with
 * intent=related. Not a hand-built mapping across 87 products that would rot
 * the moment the catalogue changes, and not "other things in the same
 * collection", which on this store would surface whatever happens to sort
 * first. Verified on the ring light: 10 related products returned, all 10 in
 * stock.
 *
 * ── BUT RELATED IS NOT THE SAME AS RELEVANT ───────────────────────────────
 * That same verified call returned, in order: a hair clipper, a ring light, a
 * work mat, a second ring light, a set of guards. Shopify's ordering is fine
 * for a "you may also like" carousel and wrong for "we do not have this, take
 * this instead" — the two ring lights were second and fourth.
 *
 * So the list is re-scored by how many meaningful words each candidate shares
 * with the sold-out product's title. Hebrew retail titles carry the product
 * type in words ("רינג תאורה", "מכונת תספורת", "מסכת שיער"), so word overlap
 * is a decent proxy for "the same kind of thing". Anything scoring zero is
 * dropped rather than shown: a work mat offered to someone who wanted a ring
 * light is worse than showing nothing, because it reads as the shop not
 * understanding what they asked for.
 *
 * ── HONESTY ───────────────────────────────────────────────────────────────
 * The heading says the product is out of stock before it offers anything.
 * Nobody is being quietly redirected — the "notify me" path stays exactly
 * where it is, above this block, because for the customer who specifically
 * wants THIS ring light back, their email address is the conversion.
 */
(function () {
  'use strict';

  var MOUNTED = 'data-nadi-alt-mounted';
  var MAX = 3;

  /* Words that carry no product meaning in a Hebrew retail title. */
  var STOP = ['מ"ל', 'מל', 'גרם', 'ליטר', 'יחידות', 'יחידה', 'מקצועי', 'מקצועית',
    'מקצועיות', 'איכותי', 'איכותית', 'חברת', 'מבית', 'דגם', 'כולל', 'עם', 'ללא',
    'לשיער', 'של', 'את', 'and', 'the', 'for', 'pro', 'professional'];

  function words(title) {
    return String(title || '')
      .toLowerCase()
      .replace(/[|,()\[\]{}"'’״׳\-–—+/\\.]/g, ' ')
      .split(/\s+/)
      .filter(function (w) { return w.length >= 3 && STOP.indexOf(w) === -1 && !/^\d+$/.test(w); });
  }

  function money(agorot) {
    return '₪' + (agorot / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  function render(host, items) {
    var box = document.createElement('div');
    box.className = 'nadi-alt';
    box.setAttribute('data-nadi-alt', '');

    var h = document.createElement('p');
    h.className = 'nadi-alt__head';
    h.innerHTML = 'המוצר אזל מהמלאי. <strong>אלה זמינים עכשיו:</strong>';
    box.appendChild(h);

    var ul = document.createElement('ul');
    ul.className = 'nadi-alt__list';
    items.forEach(function (p) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'nadi-alt__item';
      a.href = p.url;
      var img = p.featured_image || (p.images && p.images[0]);
      a.innerHTML =
        (img ? '<img class="nadi-alt__img" src="' + img + '&width=120" alt="" loading="lazy" width="52" height="52">' : '') +
        '<span class="nadi-alt__body">' +
          '<span class="nadi-alt__title">' + p.title + '</span>' +
          '<span class="nadi-alt__price">' + money(p.price) + '</span>' +
        '</span>';
      li.appendChild(a);
      ul.appendChild(li);
    });
    box.appendChild(ul);
    host.appendChild(box);
  }

  function run() {
    if (!/\/products\//.test(location.pathname)) return;

    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    /* Sold out is read from the form's own submit button, not from a guess at
       the theme's markup: Shopify disables it and Kalles keeps that. */
    var submit = form.querySelector('.t4s-product-form__submit, button[type=submit]');
    if (!submit || !(submit.disabled || submit.hasAttribute('disabled'))) return;

    var host = form.querySelector('.t4s-product-form__buttons') || form;
    if (host.hasAttribute(MOUNTED)) return;

    var meta = window.__nadiProduct || null;
    var idEl = form.querySelector('[name="id"]');
    var handle = (location.pathname.split('/products/')[1] || '').split(/[?#]/)[0];

    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        if (!product) return null;
        var mine = words(product.title);
        return fetch('/recommendations/products.json?product_id=' + product.id + '&limit=10&intent=related',
          { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) {
            if (!j || !j.products) return null;
            return j.products
              .filter(function (p) { return p.available; })
              .map(function (p) {
                /* The leading words carry the product TYPE. Hebrew retail
                   titles open with what the thing is — "רינג תאורה",
                   "מברשת פן", "מכונת תספורת" — and only then say whose it is
                   and how big. So a match on one of the first two words is
                   worth three, and a match further along is worth one.
                   Measured on the ring light: without this, a ₪39 SPIKE brush
                   scored the same as a ₪399 ring light, because both share the
                   brand name. Sharing a brand is not being the same product,
                   and offering a brush to someone who wanted a ring light
                   reads as the shop not understanding the question. */
                var theirs = words(p.title);
                var score = mine.reduce(function (n, w, i) {
                  if (theirs.indexOf(w) === -1) return n;
                  return n + (i < 2 ? 3 : 1);
                }, 0);
                return { p: p, score: score };
              })
              /* Three is one leading-word match. Below that it is a guess. */
              .filter(function (x) { return x.score >= 3; })
              .sort(function (a, b) { return b.score - a.score; })
              .slice(0, MAX)
              .map(function (x) { return x.p; });
          });
      })
      .then(function (items) {
        if (!items || !items.length) return;      /* nothing relevant — say nothing */
        if (host.hasAttribute(MOUNTED)) return;
        host.setAttribute(MOUNTED, '');
        render(host, items);
      })
      .catch(function () { /* never break a product page over this */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
